import {
  observable,
  action,
  computed,
  runInAction,
  reaction,
  get,
  extendObservable,
  flow,
  set
} from 'mobx';
import { getAllObservations } from '../apis/ObservationApi';
import { data as observations_mock } from '../__mockData__/observations.json';
import axios from 'axios';

const CancelToken = axios.CancelToken;

export default class ObservationsStore {
  clearWhenLoggedOut;
  cancelTokens = {
    getAllObservations: undefined
  };

  /**
   * Observables
   */
  @observable observationsByName = {};

  @observable selectedObservationName = undefined;

  @observable
  loading = {
    getAllObservations: false
  };

  @observable
  loadingErrors = {
    getAllObservations: false
  };

  @observable observationsHaveBeenLoaded = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
  }

  async init() {
    this.setupLogoutReaction();
  }

  /* istanbul ignore next */
  setupLogoutReaction() {
    this.clearWhenLoggedOut = reaction(
      () => this.rootStore.authStore.isLoggedIn,
      isLoggedIn => {
        if (!isLoggedIn) {
          this.reset();
        }
      },
      {
        fireImmediately: false
      }
    );
  }

  @action
  reset() {
    this.observationsByName = {};
    this.selectedObservationName = undefined;
    this.observationsHaveBeenLoaded = false;
    this.loading = {
      getAllObservations: false
    };
  }

  shouldUseMockData() {
    return this.rootStore.configStore.config[
      this.rootStore.configStore.configurationKeys.useMockDataInUi
    ];
  }

  @action
  /* istanbul ignore next */
  async loadMockObservations(delay = 1000) {
    return new Promise(resolve => {
      this.loading.getAllObservations = true;

      setTimeout(() => {
        runInAction(() => {
          observations_mock.forEach(observation => {
            extendObservable(this.observationsByName, {
              [observation.observationName]: observation
            });
          });

          this.loading.getAllObservations = false;
          this.observationsHaveBeenLoaded = true;
        });

        resolve(true);
      }, delay);
    });
  }

  getAllObservations = flow(function*(force = false) {
    if (!force && this.observationsHaveBeenLoaded) {
      return;
    }

    const useMockData = this.shouldUseMockData();
    /* istanbul ignore if  */
    if (useMockData) {
      return yield this.loadMockObservations();
    }

    try {
      this.loading.getAllObservations = true;
      const username = this.rootStore.authStore.getUserName();
      const graphQlHubServiceUrl = this.rootStore.configStore.config[
        this.rootStore.configStore.configurationKeys.graphQlHubServiceUrl
      ];

      this.cancelTokens.getAllObservations = CancelToken.source();

      const observations = yield getAllObservations(
        graphQlHubServiceUrl,
        username,
        this.cancelTokens.getAllObservations.token
      );

      observations.forEach(observation => {
        if (this.observationsByName[observation.observationName]) {
          set(
            this.observationsByName,
            observation.observationName,
            observation
          );
        } else {
          extendObservable(this.observationsByName, {
            [observation.observationName]: observation
          });
        }
      });

      this.observationsHaveBeenLoaded = true;
      this.loadingErrors.getAllObservations = false;
    } catch (error) {
      if (!axios.isCancel(error)) {
        this.loadingErrors.getAllObservations = true;
      }
    } finally {
      this.loading.getAllObservations = false;
      this.cancelTokens.getAllObservations = undefined;
    }
    return;
  });

  cancelGetAllObservations() {
    if (this.cancelTokens.getAllObservations) {
      this.cancelTokens.getAllObservations.cancel();
    }
  }

  @action
  setSelectedObservation(observationName) {
    this.selectedObservation = observationName;
  }

  @computed
  get selectedObservation() {
    if (!this.observationsHaveBeenLoaded || !this.selectedObservationName) {
      return {};
    }

    return get(this.observationsByName, this.selectedObservationName) || {};
  }

  set selectedObservation(observationName) {
    this.selectedObservationName = observationName;
  }

  @computed
  get observations() {
    return Object.keys(this.observationsByName).map(key => {
      return this.observationsByName[key];
    });
  }
}



// WEBPACK FOOTER //
// ./src/stores/ObservationsStore.js